import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-circle.svg';
import { Typography as AppTypography } from '../../../../components/Typography/Typography';
import { ROUTES } from '../../../../constants/routes';
import { QUERY_KEYS } from '../../../../queries/constants';
import { Http } from '../../../../services/Http';
import type { Timesheet, TimesheetRow, WeeklyTimesheet } from '../../../../types';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { addDaysToDate, getMonthLabelShort } from '../../../../utils/formatters';
import { getWeeklyTimesheetWeekStart } from '../../../../utils/getWeeklyTimesheetWeekStart';
import {
  buildCopyCellPatches,
  isCopyTargetEligible,
  isSubmittedTimesheetStatus,
  selectedRowsSkipSourceHolidayColumns,
  targetWeekHolidayCopyWarning,
} from '../../../../utils/copyRowsBetweenTimesheets';
import { usePendingMonthView, timesheetWeekStartsInCalendarMonth } from '../../../IssuedTimesheets/PendingMonthViewContext';
import {
  compareTimesheetsByWeekStart,
  getCalendarYearMonthFromTimesheetWeekStart,
  pendingMonthTimesheetDomId,
} from '../../../../utils/pendingTimesheetsMonthWeeks';
import { scrollToPendingMonthTimesheetAnchor } from '../../../../utils/scrollToPendingMonthTimesheetAnchor';
import { useToastMessage } from '../../../../hooks/useToastMessage';

/** Design: "Dec 4 — Dec 8, 2023" (en dash, short month, no leading zero on day). */
function formatCopyMenuWeekRange(weekStart: string, weekLength: number): string {
  if (!weekStart || !weekLength) return '';
  const [y, m, d] = weekStart.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return '';
  const start = new Date(y, m - 1, d);
  const end = addDaysToDate(weekStart, weekLength - 1);
  if (!(end instanceof Date) || isNaN(end.getTime())) return '';
  const part = (dt: Date) =>
    `${getMonthLabelShort(dt.getMonth() + 1)} ${dt.getDate()}, ${dt.getFullYear()}`;
  return `${part(start)} — ${part(end)}`;
}

type Props = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onCopied?: () => void;
  allTimesheets: Timesheet[] | undefined;
  sourceTimesheet: Timesheet | WeeklyTimesheet;
  selectedRows: TimesheetRow[];
  userId: number | undefined;
};

export function CopyRowsToWeekMenu({
  open,
  anchorEl,
  onClose,
  onCopied,
  allTimesheets,
  sourceTimesheet,
  selectedRows,
  userId,
}: Props) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pushErrorToast, pushSuccessToast } = useToastMessage();
  const monthCtx = usePendingMonthView();

  const sourceWeekStart = getWeeklyTimesheetWeekStart(sourceTimesheet);
  const sourceWeekDates = useMemo(
    () => sourceTimesheet.weekDates ?? [],
    [sourceTimesheet.weekDates]
  );

  const [pickerYear, setPickerYear] = useState(monthCtx?.viewedYear ?? new Date().getFullYear());
  const [pickerMonth, setPickerMonth] = useState(monthCtx?.viewedMonth ?? new Date().getMonth() + 1);
  const [selectedTargetIds, setSelectedTargetIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const maxY = now.getFullYear();
    const maxM = now.getMonth() + 1;
    let y = monthCtx?.viewedYear ?? maxY;
    let m = monthCtx?.viewedMonth ?? maxM;
    if (y > maxY) y = maxY;
    if (y === maxY && m > maxM) m = maxM;
    setPickerYear(y);
    setPickerMonth(m);
    setSelectedTargetIds(new Set());
  }, [open, monthCtx]);

  const yearOptions = useMemo(() => {
    const now = new Date();
    const maxYear = now.getFullYear();
    const ys = new Set<number>();
    (allTimesheets ?? []).forEach((t) => {
      const k = getWeeklyTimesheetWeekStart(t).split('T')[0];
      if (k) {
        const yr = Number(k.split('-')[0]);
        if (Number.isFinite(yr) && yr <= maxYear) ys.add(yr);
      }
    });
    const y = monthCtx?.viewedYear ?? maxYear;
    if (Number.isFinite(y - 1) && y - 1 <= maxYear) ys.add(y - 1);
    if (y <= maxYear) ys.add(y);
    if (ys.size === 0) ys.add(maxYear);
    return Array.from(ys).sort((a, b) => a - b);
  }, [allTimesheets, monthCtx?.viewedYear]);

  const allowedMonths = useMemo(() => {
    const now = new Date();
    const cy = now.getFullYear();
    const cm = now.getMonth() + 1;
    if (pickerYear < cy) return Array.from({ length: 12 }, (_, i) => i + 1);
    if (pickerYear > cy) return [];
    return Array.from({ length: cm }, (_, i) => i + 1);
  }, [pickerYear]);

  const weeksInPickerMonth = useMemo(() => {
    if (!allTimesheets) return [];
    return allTimesheets
      .filter((ts) => timesheetWeekStartsInCalendarMonth(ts, pickerYear, pickerMonth))
      .sort(compareTimesheetsByWeekStart);
  }, [allTimesheets, pickerYear, pickerMonth]);

  const eligibleIds = useMemo(
    () =>
      weeksInPickerMonth
        .filter((ts) => isCopyTargetEligible(ts, sourceTimesheet.id))
        .map((ts) => ts.id),
    [weeksInPickerMonth, sourceTimesheet.id]
  );

  const showSourceHolidayInfo = useMemo(
    () => selectedRowsSkipSourceHolidayColumns(selectedRows, sourceWeekStart, sourceWeekDates),
    [selectedRows, sourceWeekStart, sourceWeekDates]
  );

  /** MUI Select list renders in a portal; force dark surface + readable item text (fixes light menu in dark theme). */
  const copySelectMenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          bgcolor: colors.surfaceBackground.bg2,
          border: `1px solid ${colors.border.default}`,
          maxHeight: 320,
          '& .MuiMenuItem-root': {
            color: colors.text.main,
            fontSize: 14,
          },
          '& .MuiMenuItem-root.Mui-selected': {
            bgcolor: colors.surfaceInteraction.selectedLight,
            color: colors.text.main,
          },
          '& .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
            bgcolor: colors.surfaceInteraction.selectedLight,
          },
          '& .MuiMenuItem-root:hover': {
            bgcolor: colors.surfaceBackground.highlighted,
          },
          '& .MuiMenuItem-root.Mui-selected:hover': {
            bgcolor: colors.surfaceInteraction.hover,
          },
        },
      },
      anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
      transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
    }),
    [colors]
  );

  const copySelectFieldSx = useMemo(
    () => ({
      flex: 1,
      minWidth: 0,
      borderRadius: '8px',
      bgcolor: colors.surfaceBackground.bg1,
      color: colors.text.main,
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        bgcolor: colors.surfaceBackground.bg1,
      },
      '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(colors.text.main, 0.12),
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(colors.text.main, 0.22),
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.surfaceAccent.default,
      },
      '& .MuiSvgIcon-root': {
        color: alpha(colors.text.main, 0.65),
      },
    }),
    [colors]
  );

  const copyCheckboxSx = useMemo(
    () => ({
      p: 0.25,
      color: alpha(colors.text.main, 0.35),
      '&.Mui-checked': { color: colors.surfaceAccent.default },
      '&.Mui-disabled': { color: alpha(colors.text.main, 0.15) },
    }),
    [colors]
  );

  const toggleTarget = useCallback(
    (id: number, eligible: boolean) => {
      if (!eligible) return;
      setSelectedTargetIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    []
  );

  const toggleSelectAllEligible = useCallback(() => {
    const allOn = eligibleIds.length > 0 && eligibleIds.every((id) => selectedTargetIds.has(id));
    if (allOn) {
      setSelectedTargetIds(new Set());
    } else {
      setSelectedTargetIds(new Set(eligibleIds));
    }
  }, [eligibleIds, selectedTargetIds]);

  const handleSubmit = useCallback(async () => {
    if (!allTimesheets || selectedTargetIds.size === 0) return;
    const targets = allTimesheets.filter((ts) => selectedTargetIds.has(ts.id));
    const patches = buildCopyCellPatches(
      selectedRows,
      sourceWeekStart,
      sourceWeekDates,
      targets
    );
    if (patches.length === 0) {
      pushErrorToast({ message: 'No matching rows to update for the selected weeks.' });
      return;
    }
    setSubmitting(true);
    try {
      for (const p of patches) {
        await Http.axios.patch(`/timesheet-row/${p.rowId}`, p.payload);
      }
      pushSuccessToast({
        title: 'Copied',
        message: 'Values were copied to the selected week(s). Open updated timesheet.',
        actionLabel: 'Go to copied week',
        action: () => {
          const firstTarget = targets[0];
          if (!firstTarget) return;

          if (monthCtx) {
            const targetYearMonth = getCalendarYearMonthFromTimesheetWeekStart(firstTarget);
            if (!targetYearMonth) return;
            const { year: targetYear, month: targetMonth } = targetYearMonth;
            const targetAnchorId = pendingMonthTimesheetDomId(firstTarget.id);

            if (targetYear === monthCtx.viewedYear && targetMonth === monthCtx.viewedMonth) {
              scrollToPendingMonthTimesheetAnchor(targetAnchorId);
              return;
            }

            navigate(
              `/${ROUTES.PENDING_TIMESHEETS}/month/${targetYear}/${targetMonth}#${targetAnchorId}`
            );
            return;
          }

          navigate(`/${ROUTES.PENDING_TIMESHEETS}/${firstTarget.id}`);
        },
      });
      onClose();
      setSelectedTargetIds(new Set());
      onCopied?.();
      // Do not await: invalidation refetches can take seconds and would keep the submit loader running.
      void queryClient.invalidateQueries([QUERY_KEYS.TIMESHEETS, userId]);
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SINGLE_TIMESHEET] });
    } catch (e) {
      pushErrorToast({ message: extractErrorMessage(e) });
    } finally {
      setSubmitting(false);
    }
  }, [
    allTimesheets,
    selectedTargetIds,
    selectedRows,
    sourceWeekStart,
    sourceWeekDates,
    pushErrorToast,
    pushSuccessToast,
    queryClient,
    userId,
    onClose,
    monthCtx,
    navigate,
    onCopied,
  ]);

  const handlePopoverClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  const allEligibleOn =
    eligibleIds.length > 0 && eligibleIds.every((id) => selectedTargetIds.has(id));
  const someEligibleOn = eligibleIds.some((id) => selectedTargetIds.has(id));

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            minWidth: 320,
            maxWidth: 400,
            maxHeight: 'min(80vh, 520px)',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: colors.surfaceBackground.bg4 ?? colors.surfaceBackground.bg2,
            border: `1px solid ${alpha(colors.border.default, 0.5)}`,
            borderRadius: '12px',
            boxShadow: `0 16px 48px ${alpha('#000000', 0.5)}`,
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'auto',
          flex: 1,
        }}
      >
        {showSourceHolidayInfo && (
          <Box
            sx={{
              py: 1,
              px: 1.5,
              borderRadius: '8px',
              bgcolor: alpha(colors.textStatus.warning, 0.12),
              color: colors.textStatus.warning,
              border: `1px solid ${alpha(colors.textStatus.warning, 0.28)}`,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            Selected rows have a holiday; holidays won&apos;t be copied.
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size='small' sx={{ flex: 1, minWidth: 0 }}>
            <Select
              displayEmpty
              value={pickerMonth}
              aria-label='Month'
              variant='outlined'
              MenuProps={copySelectMenuProps}
              sx={copySelectFieldSx}
              renderValue={(v) =>
                new Date(2000, Number(v) - 1, 1).toLocaleString('en-US', { month: 'short' })
              }
              onChange={(e) => {
                setPickerMonth(Number(e.target.value));
                setSelectedTargetIds(new Set());
              }}
            >
              {allowedMonths.map((m) => (
                <MenuItem key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'short' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size='small' sx={{ flex: 1, minWidth: 0 }}>
            <Select
              displayEmpty
              value={pickerYear}
              aria-label='Year'
              variant='outlined'
              MenuProps={copySelectMenuProps}
              sx={copySelectFieldSx}
              renderValue={(v) => String(v)}
              onChange={(e) => {
                const y = Number(e.target.value);
                setPickerYear(y);
                const now = new Date();
                const cy = now.getFullYear();
                const cm = now.getMonth() + 1;
                if (y === cy) {
                  setPickerMonth((prev) => (prev > cm ? cm : prev));
                }
                setSelectedTargetIds(new Set());
              }}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            py: 0.75,
            borderBottom: `1px solid ${alpha(colors.border.default, 0.35)}`,
          }}
        >
          <Checkbox
            size='small'
            checked={allEligibleOn}
            indeterminate={someEligibleOn && !allEligibleOn}
            onChange={toggleSelectAllEligible}
            disabled={eligibleIds.length === 0}
            sx={copyCheckboxSx}
          />
          <AppTypography variant='body' color={colors.text.main} style={{ fontWeight: 500, fontSize: 15 }}>
            Select all
          </AppTypography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            maxHeight: 260,
            overflowY: 'auto',
            mx: -0.5,
          }}
        >
          {weeksInPickerMonth.length === 0 && (
            <Box sx={{ px: 1 }}>
              <AppTypography variant='body' color={colors.text.secondary}>
                No weeks in this month.
              </AppTypography>
            </Box>
          )}
          {weeksInPickerMonth.map((ts) => {
            const ws = getWeeklyTimesheetWeekStart(ts);
            const len = (ts.weekDates?.length ?? 0) + (ts.holidays?.length ?? 0);
            const label = formatCopyMenuWeekRange(ws, len || 7);
            const eligible = isCopyTargetEligible(ts, sourceTimesheet.id);
            const submitted = isSubmittedTimesheetStatus(ts.status);
            const grey = submitted || !eligible;
            const holidayWarn = targetWeekHolidayCopyWarning(ts);
            const checked = selectedTargetIds.has(ts.id) && eligible;
            return (
              <Box
                key={ts.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  py: 1,
                  px: 1.5,
                  borderRadius: '8px',
                  opacity: grey ? 0.42 : 1,
                  bgcolor: checked
                    ? alpha(colors.surfaceAccent.default, 0.2)
                    : holidayWarn && eligible
                    ? 'rgba(232, 165, 75, 0.12)'
                    : 'transparent',
                  outline: checked ? `1px solid ${alpha(colors.surfaceAccent.default, 0.45)}` : 'none',
                  outlineOffset: -1,
                }}
              >
                <Checkbox
                  size='small'
                  checked={checked}
                  disabled={!eligible}
                  onChange={() => toggleTarget(ts.id, eligible)}
                  sx={copyCheckboxSx}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: colors.text.main,
                      fontSize: 14,
                      fontWeight: checked ? 600 : 400,
                      letterSpacing: '0.01em',
                    }}
                  >
                    {label}
                  </Typography>
                  {submitted && (
                    <Typography variant='caption' sx={{ color: colors.text.secondary, display: 'block', mt: 0.25 }}>
                      Submitted
                    </Typography>
                  )}
                </Box>
                {holidayWarn && eligible && (
                  <Tooltip title="This week has a holiday; values won't be copied to that day.">
                    <IconButton size='small' sx={{ color: colors.textStatus.warning, p: 0.25 }}>
                      <InfoIcon style={{ width: 18, height: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ pt: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant='contained'
            disableElevation
            disabled={submitting || selectedTargetIds.size === 0}
            onClick={() => void handleSubmit()}
            sx={{
              bgcolor: colors.surfaceAccent.default,
              color: colors.icon.inverse,
              fontWeight: 700,
              fontSize: 15,
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              py: 1,
              minWidth: 140,
              boxShadow: 'none',
              '&:hover': { bgcolor: colors.surfaceAccent.hover, boxShadow: 'none' },
              '&.Mui-disabled': {
                bgcolor: alpha(colors.surfaceAccent.default, 0.35),
                color: alpha(colors.icon.inverse, 0.5),
              },
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <CircularProgress size={18} thickness={5} sx={{ color: colors.icon.inverse }} />
                Copying…
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
